import { useEffect, useRef } from "react";
import { useCallbackRef } from "ui";

interface SectionRef {
  id: string;
  ref: React.RefObject<HTMLElement | HTMLDivElement>;
}

interface UseScrollSpyOptions {
  sectionRefs: SectionRef[];
  onActiveChange: (activeId: string) => void;
  rootMargin?: string;
  enabled?: boolean;
}

export function useScrollSpy({
  sectionRefs,
  onActiveChange,
  rootMargin = "-180px 0px -50% 0px",
  enabled = true,
}: UseScrollSpyOptions): void {
  const activeIdRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  const onActiveChangeRef = useCallbackRef(onActiveChange);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Track intersection entries for all sections
    const intersectionMap = new Map<
      string,
      { ratio: number; element: Element }
    >();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = sectionRefs.find(
            (section) => section.ref.current === entry.target
          )?.id;

          if (sectionId) {
            if (entry.isIntersecting) {
              intersectionMap.set(sectionId, {
                ratio: entry.intersectionRatio,
                element: entry.target,
              });
            } else {
              intersectionMap.delete(sectionId);
            }
          }
        });

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          // Find the section with the highest intersection ratio
          let maxRatio = 0;
          let newActiveId: string | null = null;

          intersectionMap.forEach((value, key) => {
            if (value.ratio > maxRatio) {
              maxRatio = value.ratio;
              newActiveId = key;
            } else if (value.ratio === maxRatio && newActiveId) {
              // If ratios are equal, prefer the topmost section
              const currentElement = value.element;
              const activeElement = intersectionMap.get(newActiveId)?.element;

              if (currentElement && activeElement) {
                const currentTop = currentElement.getBoundingClientRect().top;
                const activeTop = activeElement.getBoundingClientRect().top;

                if (currentTop < activeTop) {
                  newActiveId = key;
                }
              }
            }
          });

          // Only trigger callback if active section actually changed
          if (newActiveId && newActiveId !== activeIdRef.current) {
            activeIdRef.current = newActiveId;
            onActiveChangeRef(newActiveId);
          }
        }, 100);
      },
      {
        rootMargin,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sectionRefs.forEach((section) => {
      if (section.ref.current) {
        observer.observe(section.ref.current);
      }
    });

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      observer.disconnect();
    };
  }, [sectionRefs, onActiveChangeRef, rootMargin, enabled]);
}
